import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Crown, Gamepad2, Gift, Zap, Home, X, Bell, BellRing, Eye, EyeOff, User, Megaphone, MessageCircle, Users, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../../components/ui/button';
import '../../styles/player-animations.css';

// Component imports
import HomeTab from './HomeTab';
import GamesTab from './GamesTab';
import RewardsTab from './RewardsTab';
import ReferralsTab from './ReferralsTab';
import SettingsTab from './SettingsTab';
import ProfileTab from './ProfileTab';
import BrandTab from './BrandTab';
import ChatTab from './ChatTab';
import DepositWithdrawPopup from './DepositWithdrawPopup';
import PhoneVerificationGate from './PhoneVerificationGate';
import PlayerBottomNav from './PlayerBottomNav';
import WalletNotificationModal from '../../components/WalletNotificationModal';
import LevelUpOverlay from '../../components/LevelUpOverlay';
import AnimatedCounter from '../../components/AnimatedCounter';
import SuccessBurst from '../../components/SuccessBurst';
import { getTierTheme } from './TierTheme';
import { trackEvent, trackPageView } from '../../lib/analytics';
import CasinoAtmosphere from './CasinoAtmosphere';
import PirateLobbyExperience from './PirateLobbyExperience';
import '../../styles/casino-redesign.css';
import '../../styles/pirate-game-lobby-v3.css';
import '../../styles/pirate-live-fixes.css';
import '../../styles/player-mobile-layout.css';
import '../../styles/player-lobby-fit-lock.css';
import '../../styles/pirate-live-final-overrides.css';
import { getCasinoTheme } from '../../theme/casinoThemes';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function mediaUrl(url) {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
}

const TABS = [
  { id: 'home', label: 'Lobby', icon: Home },
  { id: 'games', label: 'Games', icon: Gamepad2 },
  { id: 'referrals', label: 'Referrals', icon: Users },
  { id: 'rewards', label: 'Rewards', icon: Gift },
  { id: 'profile', label: 'Profile', icon: User },
];
const VALID_PLAYER_TABS = new Set([...TABS.map((tab) => tab.id), 'settings', 'profile', 'brand', 'chat']);

export default function PlayerApp() {
  const casinoTheme = getCasinoTheme();
  const { user, logout, checkAuth, tenantSlug: authTenantSlug } = useAuth();
  const params = useParams();
  const tenantSlug = params.tenantSlug || authTenantSlug || null;
  // ── Swipe gesture navigation with drag-follow (OS-like continuous swipe) ──
  const TAB_ORDER = ['home', 'games', 'referrals', 'rewards', 'profile'];
  const touchRef = useRef({ startX: 0, startY: 0, startTime: 0, tracking: false, locked: null });
  const mainRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const urlTab = new URLSearchParams(window.location.search || '').get('tab');
      if (urlTab && VALID_PLAYER_TABS.has(urlTab)) return urlTab;
      const saved = localStorage.getItem('player_active_tab');
      if (saved && VALID_PLAYER_TABS.has(saved)) return saved;
    } catch {}
    return 'home';
  });
  const [prevTab, setPrevTab] = useState('home');
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [headerSoundOn, setHeaderSoundOn] = useState(() => {
    try { return localStorage.getItem('vl_pirate_sound') === 'on'; } catch { return false; }
  });
  const pullRef = useRef({ startY: 0, tracking: false, activePanel: null });

  // Persist active tab
  useEffect(() => {
    try { localStorage.setItem('player_active_tab', activeTab); } catch {}
    const path = `${window.location.pathname}?tab=${activeTab}`;
    trackPageView(path, `Vegas Lobby ${activeTab}`);
    trackEvent('player_tab_view', { tab: activeTab, page_path: path });
  }, [activeTab]);

  // Double-tap header → scroll active panel to top
  const lastHeaderTapRef = useRef(0);
  const handleHeaderDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastHeaderTapRef.current < 400) {
      // Double-tap detected — scroll active panel to top
      const panel = document.querySelector(`[data-testid="swipe-panel-${activeTab}"]`);
      if (panel) {
        panel.scrollTo({ top: 0, behavior: 'smooth' });
      }
      lastHeaderTapRef.current = 0;
    } else {
      lastHeaderTapRef.current = now;
    }
  }, [activeTab]);

  function openLiveSupport() {
    setDepositModal(null);
    setPrevTab(activeTab);
    setActiveTab('chat');
    setChatUnread(0);
    trackEvent('live_support_open', { source: 'vegas_lobby_chat' });
  }

  const switchTab = (tab) => {
    setDepositModal(null);
    if (tab === activeTab) return;
    setPrevTab(activeTab);
    setActiveTab(tab);
  };
  const goBack = () => {
    setDepositModal(null);
    setActiveTab((current) => (prevTab && prevTab !== current ? prevTab : 'home'));
  };

  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, startTime: Date.now(), tracking: false, locked: null };
    setDragX(0);
    // Pull-to-refresh: only start tracking if the currently active tab panel is scrolled at top
    const panel = e.currentTarget.querySelector(`[data-testid="swipe-panel-${activeTab}"]`);
    pullRef.current = {
      startY: t.clientY,
      tracking: false,
      activePanel: panel,
      scrollTop: panel ? panel.scrollTop : 0,
    };
  }, [activeTab]);

  const handleTouchMove = useCallback((e) => {
    const t = e.touches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;
    // Detect axis lock after initial movement
    if (!touchRef.current.locked && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      touchRef.current.locked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }
    if (touchRef.current.locked === 'horizontal') {
      touchRef.current.tracking = true;
      const curIdx = TAB_ORDER.indexOf(activeTab);
      const atStart = curIdx === 0 && dx > 0;
      const atEnd = curIdx === TAB_ORDER.length - 1 && dx < 0;
      const dampened = (atStart || atEnd) ? dx * 0.25 : dx;
      setDragX(dampened);
      setDragging(true);
    } else if (touchRef.current.locked === 'vertical' && dy > 0 && !refreshing) {
      // Pull-to-refresh: only when panel is at top AND pulling down
      const panel = pullRef.current.activePanel;
      if (panel && pullRef.current.scrollTop <= 0 && panel.scrollTop <= 0) {
        pullRef.current.tracking = true;
        // Dampen the pull
        setPullDistance(Math.min(dy * 0.5, 120));
      }
    }
  }, [activeTab, refreshing]);

  const handleTouchEnd = useCallback((e) => {
    // Pull-to-refresh end
    if (pullRef.current.tracking) {
      pullRef.current.tracking = false;
      if (pullDistance > 60) {
        setRefreshing(true);
        window.dispatchEvent(new CustomEvent('vl:player-soft-refresh'));
      } else {
        setPullDistance(0);
      }
    }
    if (!touchRef.current.tracking) {
      setDragX(0);
      setDragging(false);
      return;
    }
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dt = Date.now() - touchRef.current.startTime;
    const velocity = Math.abs(dx) / Math.max(dt, 1);
    touchRef.current.tracking = false;
    touchRef.current.locked = null;

    const width = mainRef.current?.clientWidth || window.innerWidth;
    const passedThreshold = Math.abs(dx) > width * 0.25 || velocity > 0.5;
    const curIdx = TAB_ORDER.indexOf(activeTab);
    if (passedThreshold && curIdx !== -1) {
      if (dx < 0 && curIdx < TAB_ORDER.length - 1) {
        setPrevTab(activeTab);
        setActiveTab(TAB_ORDER[curIdx + 1]);
      } else if (dx > 0 && curIdx > 0) {
        setPrevTab(activeTab);
        setActiveTab(TAB_ORDER[curIdx - 1]);
      }
    }
    setDragX(0);
    setDragging(false);
  }, [activeTab, pullDistance]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ games: [], methods: [], transactions: [], announcements: [], homeBanners: [], accounts: [], ledger: [] });
  const [chatUnread, setChatUnread] = useState(0);
  const [wallet, setWallet] = useState({ balance: 0, play_credits: 0, pending: 0 });
  const [myTier, setMyTier] = useState(null);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showAnnouncement, setShowAnnouncement] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  // Announcements dropdown (header bell beside notifications)
  const [showAnnDrop, setShowAnnDrop] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [supportChannels, setSupportChannels] = useState({});
  // Track which announcement IDs this player has opened so we can show an
  // "unseen" gold dot on the bell. Persisted in localStorage.
  const seenAnnKey = `vl_seen_announcements_${user?.id || 'anon'}`;
  const [seenAnnIds, setSeenAnnIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(seenAnnKey) || '[]')); } catch { return new Set(); }
  });
  const markAnnSeen = (id) => {
    if (!id) return;
    setSeenAnnIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem(seenAnnKey, JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };
  const unseenCount = announcements.reduce((n, a) => n + (seenAnnIds.has(a.id) ? 0 : 1), 0);

  // Deposit flow notifications: `pending` shown right after the player
  // submits a manual deposit request, automatically swapped to `credited`
  // once we detect the wallet balance actually went up (gateway auto-sync
  // or manual admin approval). State shape:
  // Player "wallet loaded" modal state. Set when a deposit is submitted,
  // flipped to `credited` only when the BACKEND confirms the specific
  // request was approved (admin approval for manual, gateway settlement
  // for instant). We NEVER watch the wallet balance delta — that was
  // prone to false-positives whenever an unrelated credit (bonus, promo,
  // referral, another approved deposit) moved the balance.
  //   { variant: 'pending'|'credited'|'rejected', txnId, submittedAt,
  //     amountUsd, creditedDelta?, rejectedReason? }
  const [walletNotif, setWalletNotif] = useState(null);
  const walletNotifRef = useRef(null);
  walletNotifRef.current = walletNotif;

  const handleDepositSubmitted = (kind, info = {}) => {
    if (kind === 'deposit') {
      trackEvent('deposit_request_submitted', {
        amount: Number(info.usdAmount ?? info.amountUsd ?? info.scAmount ?? 0),
        instant: !!info.instant,
      });
      setWalletNotif({
        variant: 'pending',
        submittedAt: Date.now(),
        amountUsd: Number(info.usdAmount ?? info.amountUsd ?? info.scAmount ?? 0),
        txnId: info.txnId,
        instant: !!info.instant,
      });
    } else {
      trackEvent('withdrawal_request_submitted');
      switchTab('rewards');
    }
  };

  const goToReferralRewards = () => {
    setRewardsInitialSubTab(null);
    switchTab('referrals');
  };

  // Poll the specific deposit request's status until the backend confirms
  // it was approved/rejected. Interval grows from 3s → 8s → 15s to keep
  // traffic low on long waits. Stops after 30 minutes.
  useEffect(() => {
    const n = walletNotifRef.current;
    if (!n || n.variant !== 'pending' || !n.txnId) return;

    let cancelled = false;
    let attempts = 0;

    const pollOnce = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await api.get(`/api/requests/${n.txnId}/status`);
        const data = res.data || {};
        const status = (data.status || '').toLowerCase();
        if (status === 'approved' || status === 'completed') {
          const deltaUsd = data.gross_amount_usd
            ?? data.net_amount
            ?? data.amount
            ?? n.amountUsd;
          setWalletNotif({
            variant: 'credited',
            amountUsd: n.amountUsd,
            creditedDelta: Number(Number(deltaUsd || 0).toFixed(2)),
            txnId: n.txnId,
          });
          // Refresh wallet so the balance UI also updates now that we
          // know the credit is real.
          try { await fetchData(); } catch {}
          return;
        }
        if (status === 'rejected' || status === 'failed' || status === 'cancelled') {
          setWalletNotif({
            variant: 'rejected',
            amountUsd: n.amountUsd,
            rejectedReason: data.rejected_reason || data.admin_notes || 'Request was rejected',
            txnId: n.txnId,
          });
          return;
        }
      } catch {
        // Swallow — network blips should not dismiss the pending modal.
      }
      // Back-off schedule: first minute every 3s, next 5 min every 8s,
      // then every 15s until the 30-min cap.
      let delay = 15000;
      if (attempts < 20) delay = 3000;
      else if (attempts < 60) delay = 8000;
      if (!cancelled) setTimeout(pollOnce, delay);
    };

    // First poll after 2s so instant gateways don't wait 3s on the happy path.
    const t = setTimeout(pollOnce, 2000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [walletNotif?.txnId, walletNotif?.variant]);

  useEffect(() => {
    const n = walletNotifRef.current;
    if (!n || n.variant !== 'pending') return;
    const t = setTimeout(() => {
      if (walletNotifRef.current?.variant === 'pending') setWalletNotif(null);
    }, 30 * 60 * 1000);
    return () => clearTimeout(t);
  }, [walletNotif?.submittedAt]);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [showAllNotifs, setShowAllNotifs] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(null);
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [levelUpData, setLevelUpData] = useState(null);
  // Iter 137: shared SuccessBurst trigger for celebratory moments
  // (bonus claim, deposit confirmed, account created). Pass null to hide.
  const [successBurst, setSuccessBurst] = useState(null);
  const [depositModal, setDepositModal] = useState(null); // 'deposit' | 'withdrawal' | null
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [rewardsInitialSubTab, setRewardsInitialSubTab] = useState(null);
  const [viewport, setViewport] = useState(() => ({
    height: (typeof window !== 'undefined' && window.visualViewport?.height) || (typeof window !== 'undefined' ? window.innerHeight : 0),
    offsetTop: 0,
    keyboardOpen: false,
  }));
  const prevTierRef = useRef(null);

  // Track visualViewport exactly — pin the player-app to the visible viewport.
  // This eliminates the gap between bottom-nav and on-screen keyboard.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => {
      const layoutHeight = window.innerHeight || vv.height;
      const keyboardOpen = layoutHeight - vv.height > 150;
      setViewport({ height: vv.height, offsetTop: vv.offsetTop || 0, keyboardOpen });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Lock body scroll so the fixed player-app is the only scroll context
  useEffect(() => {
    const prev = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.html;
      document.body.style.overflow = prev.body;
    };
  }, []);

  const baseTierTheme = getTierTheme(myTier?.tier_name || 'Bronze');
  // Override tier theme with tenant branding colors if present
  const tierTheme = tenantBranding ? {
    ...baseTierTheme,
    primary: tenantBranding.primary_color || baseTierTheme.primary,
    accent: tenantBranding.secondary_color || baseTierTheme.accent,
  } : baseTierTheme;
  const tenantLogoUrl = tenantBranding?.logo_url || null;
  const tenantName = tenantBranding?.name || 'Vegas Lobby';
  const avatarSrc = mediaUrl(user?.avatar_url);
  const avatarInitial = String(user?.username || user?.full_name || user?.email || 'V').charAt(0).toUpperCase();
  useEffect(() => {
    fetchData();
    fetchUnread();
    fetchMyTier();
    fetchNotifCount();
    fetchAnnouncements();
    fetchSupportChannels();
    checkTxnNotifications();
    if (tenantSlug) fetchTenantBranding();
    const unreadInterval = setInterval(fetchUnread, 10000);
    const notifInterval = setInterval(fetchNotifCount, 15000);
    const annInterval = setInterval(fetchAnnouncements, 60000);
    const txnNotifInterval = setInterval(checkTxnNotifications, 15000);
    return () => {
      clearInterval(unreadInterval);
      clearInterval(notifInterval);
      clearInterval(annInterval);
      clearInterval(txnNotifInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep notification permission user-initiated. The header bell/announcement
  // actions are visible, but the lobby should not interrupt first clicks with
  // a permission modal.
  useEffect(() => {
    setShowPushPrompt(false);
  }, []);

  const dismissPushPrompt = () => {
    localStorage.setItem('vl_push_prompt_seen', '1');
    setShowPushPrompt(false);
  };

  // ── Post-payment return handler ─────────────────────────────────────────
  // Linkgen (and manual gateway checkouts) redirect back to the app with
  // ?tab=wallet&deposit=<order_id> once the provider page completes. We:
  //   1. Force-switch to the Wallet tab so the player sees their balance.
  //   2. Poll /poll-status to trigger the idempotent settle→credit pipeline
  //      even if the webhook hasn't fired yet.
  //   3. Refresh auth/wallet so the new balance shows immediately.
  //   4. Strip the query params from the URL so a later refresh doesn't
  //      re-run the flow.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const search = window.location.search;
    if (!search) return;
    const params = new URLSearchParams(search);
    const depositId = params.get('deposit');
    const requestedTab = params.get('tab');
    const wantsTab = requestedTab && VALID_PLAYER_TABS.has(requestedTab);
    const wantsStreak = params.get('streak') === 'claim';
    if (!depositId && !wantsTab && !wantsStreak) return;

    if (wantsTab) setActiveTab(requestedTab);
    // Streak-reminder push deep-links here — force the player onto the
    // Home tab so they see the FP streak card + golden pulse immediately.
    if (wantsStreak) setActiveTab('home');

    // Clean the URL right away — don't leave the deposit id hanging around
    // in browser history (avoids replay if the user hits back).
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('tab');
      url.searchParams.delete('deposit');
      url.searchParams.delete('streak');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch {}

    if (!depositId) return;

    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      if (cancelled || attempts > 30) return;  // 30s max (1s ticks)
      try {
        const res = await api.post(`/api/deposits/${depositId}/poll-status`);
        const status = res.data?.status;
        const amt = res.data?.amount;
        if (status === 'APPROVED') {
          const usd = Number(amt || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
          toast.success(`Payment confirmed — $${usd} loaded to your wallet!`);
          try { await checkAuth?.(); } catch {}
          try { await fetchData(); } catch {}
          return;
        }
        if (status === 'FAILED' || status === 'EXPIRED') {
          toast.error(status === 'EXPIRED' ? 'Payment link expired' : 'Payment failed');
          return;
        }
      } catch {
        // transient — keep trying
      }
      setTimeout(poll, 1000);
    };
    // Kick off the polling loop after a tiny delay so the UI settles first
    setTimeout(poll, 300);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isImageBanner = (item) => ['home_banner', 'login_banner'].includes(item?.type || item?.banner_type);
  const isPopupAnnouncement = (item) => !isImageBanner(item);

  // Live transaction status polling — runs only when the Wallet tab is
  // active AND the player has at least one pending transaction. Ticks every
  // 6 seconds, and auto-stops the moment everything settles so we never spin
  // cycles in the background.
  useEffect(() => {
    if (activeTab !== 'wallet') return;
    const txns = data.transactions || [];
    const hasPending = txns.some(t => (t.status || '').toLowerCase() === 'pending');
    if (!hasPending) return;
    const id = setInterval(() => { refreshTransactions(); }, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, data.transactions]);

  const fetchMyTier = async () => {
    try {
      const r = await api.get('/api/tiers/my-tier');
      const newTierName = r.data?.tier_name;
      if (!newTierName) {
        setMyTier(r.data);
        return;
      }
      // Iter 137: persist "highest tier we've already celebrated" in
      // localStorage so the overlay fires ONLY the first time the player
      // crosses into a new tier — even if they reload the page mid-flight
      // or come back days later. The previous in-memory ref is kept as a
      // belt-and-braces fallback for the same session.
      const lsKey = `vl_celebrated_tier_${user?.id || 'anon'}`;
      let lastCelebrated = null;
      try { lastCelebrated = localStorage.getItem(lsKey); } catch {}

      const sessionPrev = prevTierRef.current;
      const isFirstFetch = !sessionPrev && !lastCelebrated;

      const isUpgrade = newTierName !== (lastCelebrated || sessionPrev);
      // Only fire on an actual upgrade (not first fetch of a returning
      // player who has already seen their current tier). Sort_order
      // comparison would be ideal but we don't have all tiers loaded
      // here — the lsKey acts as our "shown for this tier" memo.
      if (!isFirstFetch && isUpgrade && lastCelebrated && lastCelebrated !== newTierName) {
        setLevelUpData({
          from: lastCelebrated || sessionPrev,
          to: newTierName,
          color: r.data?.tier_color || '#D4AF37',
          emoji: r.data?.tier_emoji || '',
          benefits: r.data?.current_tier?.benefits
                  || r.data?.current_tier?.perks
                  || [],
          dailyBonusPct: r.data?.current_tier?.daily_bonus_pct,
        });
        try { localStorage.setItem(lsKey, newTierName); } catch {}
      } else if (!lastCelebrated) {
        // First fetch ever for this player on this device — silently seed
        // the marker so we don't accidentally fire next time they log in.
        try { localStorage.setItem(lsKey, newTierName); } catch {}
      }
      prevTierRef.current = newTierName;
      setMyTier(r.data);
    } catch {}
  };

  const fetchTenantBranding = async () => {
    try {
      const r = await api.get(`/api/tenants/public/${tenantSlug}`);
      const t = r.data.tenant || r.data;
      if (t?.branding) setTenantBranding({ ...t.branding, name: t.name });
    } catch {}
  };

  const fetchNotifCount = async () => {
    try { const r = await api.get('/api/notifications/unread-count'); setNotifCount(r.data.count || 0); } catch {}
  };

  const fetchNotifications = async () => {
    try { const r = await api.get('/api/notifications?limit=20'); setNotifications(r.data.notifications || []); } catch {}
  };

  // Iter 137: expose setSuccessBurst on window so tabs (GamesTab,
  // DepositWithdrawPopup) can trigger the celebration without prop-drilling.
  // Named scope so it's discoverable in DevTools.
  useEffect(() => {
    window.__vlTriggerBurst = (opts) => setSuccessBurst(opts);
    return () => { try { delete window.__vlTriggerBurst; } catch {} };
  }, []);

  // Watch for freshly approved/rejected deposits & withdrawals and auto-open
  // the celebration/rejection popup — even if the approval happened from
  // Telegram, another device, or while the player had the tab closed.
  //
  // Strategy: poll /api/notifications every 15 s (same cadence as the bell).
  // We compare each `*_approved` / `*_rejected` notification's id against a
  // localStorage-persisted "last-seen" set (`vl_seen_txn_notifs_{userId}`).
  // First run after a fresh login seeds the set silently so we don't blast
  // the player with a modal for an event they already acknowledged.
  const seenTxnNotifsKey = `vl_seen_txn_notifs_${user?.id || 'anon'}`;
  const seenTxnNotifsInitedRef = useRef(false);

  const checkTxnNotifications = async () => {
    if (!user?.id) return;
    try {
      const r = await api.get('/api/notifications?limit=20');
      const items = r.data.notifications || [];
      const relevant = items.filter(n => /^(deposit|withdrawal)_(approved|rejected)$/.test(n.type || ''));
      let seen;
      try { seen = new Set(JSON.parse(localStorage.getItem(seenTxnNotifsKey) || '[]')); } catch { seen = new Set(); }

      // First run after mount: seed the set silently (player already saw these).
      if (!seenTxnNotifsInitedRef.current) {
        seenTxnNotifsInitedRef.current = true;
        const all = relevant.map(n => n.id);
        try { localStorage.setItem(seenTxnNotifsKey, JSON.stringify(all)); } catch {}
        return;
      }

      // Find the NEWEST unseen approved/rejected notification.
      const fresh = relevant.find(n => !seen.has(n.id));
      if (!fresh) return;

      // Only auto-popup if there's no active flow modal already (don't
      // clobber the "please wait" pending card from an in-progress submit).
      if (!walletNotifRef.current || walletNotifRef.current.variant === 'credited' || walletNotifRef.current.variant === 'rejected') {
        const isDeposit = fresh.type.startsWith('deposit_');
        const isApproved = fresh.type.endsWith('_approved');
        // Parse amount from notification message, e.g. "Your deposit of $100 has been approved."
        const msg = fresh.message || '';
        const usdMatch = msg.match(/\$([\d,]+(?:\.\d+)?)/);
        const usd = usdMatch ? parseFloat(usdMatch[1].replace(/,/g, '')) : 0;
        const creditedDelta = Number(usd.toFixed(2));
        setWalletNotif({
          variant: isApproved ? 'credited' : 'rejected',
          amountUsd: creditedDelta,
          creditedDelta: isApproved ? creditedDelta : undefined,
          rejectedReason: isApproved ? undefined : (fresh.message || 'Request rejected'),
          txnId: `notif-${fresh.id}`,
          kind: isDeposit ? 'deposit' : 'withdrawal',
        });
        // Refresh wallet to reflect the credited balance.
        try { await fetchData(); } catch {}
      }

      // Persist ALL currently-visible relevant notifications as seen.
      const all = relevant.map(n => n.id);
      try { localStorage.setItem(seenTxnNotifsKey, JSON.stringify(all)); } catch {}
    } catch {}
  };

  const markAllRead = async () => {
    try { await api.post('/api/notifications/mark-all-read'); setNotifCount(0); setNotifications(n => n.map(x => ({ ...x, read: true }))); } catch {}
  };

  // Announcements — fetch popup/list announcements only. Image banners live on
  // the landing/home surfaces and are intentionally filtered out here.
  // Uses the same public GET /api/announcements endpoint; scopes to tenant via slug when present.
  const fetchAnnouncements = async () => {
    try {
      const qs = tenantSlug ? `?tenant_slug=${encodeURIComponent(tenantSlug)}` : '';
      const r = await api.get(`/api/announcements${qs}`);
      const items = (r.data.announcements || []).filter(isPopupAnnouncement);
      // Sort newest first and cap at 20 for scroll, dropdown shows 5.
      items.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setAnnouncements(items.slice(0, 20));
    } catch {
      setAnnouncements([]);
    }
  };

  const handleEnablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        // Register service worker and subscribe to push
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.register('/sw.js');
          const vapidRes = await api.get('/api/push/vapid-key');
          const vapidKey = vapidRes.data.public_key;
          if (vapidKey) {
            const urlBase64 = vapidKey.replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - urlBase64.length % 4) % 4);
            const rawKey = Uint8Array.from(atob(urlBase64 + padding), c => c.charCodeAt(0));
            const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
            const subJson = sub.toJSON();
            await api.post('/api/push/subscribe', { endpoint: subJson.endpoint, keys: subJson.keys });
          }
        }
        toast.success('Push notifications enabled!');
      }
    } catch (err) { console.warn('Push setup error:', err); }
    localStorage.setItem('vl_push_prompt_seen', '1');
    setShowPushPrompt(false);
  };

  const dismissAnnouncement = (annId) => {
    const dismissed = JSON.parse(sessionStorage.getItem('vl_dismissed_ann') || '[]');
    dismissed.push(annId);
    sessionStorage.setItem('vl_dismissed_ann', JSON.stringify(dismissed));
    setShowAnnouncement(null);
  };

  const fetchUnread = async () => {
    try {
      const res = await api.get('/api/chat/unread-count');
      setChatUnread(res.data.unread_count || 0);
    } catch (err) { /* ignore */ }
  };

  const fetchSupportChannels = async () => {
    try {
      const res = await api.get('/api/public/settings/support-channels');
      setSupportChannels(res.data.support_channels || {});
    } catch {
      setSupportChannels({});
    }
  };

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [gamesRes, depositMethodsRes, withdrawMethodsRes, txnsRes, homeBannerRes, popupAnnRes, accsRes, ledgerRes, walletRes] = await Promise.all([
        api.get('/api/games/list').catch(() => ({ data: { games: [] } })),
        api.get('/api/payment-methods?flow=deposit').catch(() => ({ data: { items: [] } })),
        api.get('/api/payment-methods?flow=withdraw').catch(() => ({ data: { items: [] } })),
        api.get('/api/requests/my-requests?limit=20').catch(() => ({ data: { requests: [] } })),
        api.get(`/api/announcements?banner_type=home_banner${tenantSlug ? `&tenant_slug=${tenantSlug}` : ''}`).catch(() => ({ data: { announcements: [] } })),
        api.get(`/api/announcements${tenantSlug ? `?tenant_slug=${tenantSlug}` : ''}`).catch(() => ({ data: { announcements: [] } })),
        api.get('/api/games/accounts').catch(() => ({ data: { accounts: [] } })),
        api.get('/api/wallet/transactions?limit=20').catch(() => ({ data: { transactions: [] } })),
        api.get('/api/wallet/balance').catch(() => ({ data: { balance: 0 } })),
      ]);
      const depositMethods = depositMethodsRes.data.items || depositMethodsRes.data.methods || [];
      const withdrawMethods = withdrawMethodsRes.data.items || withdrawMethodsRes.data.methods || [];
      const paymentMethods = Array.from(
        new Map([...depositMethods, ...withdrawMethods].map((method) => [method.id, method])).values()
      );
      setWallet(walletRes.data);
      setData({
        games: gamesRes.data.games || [],
        methods: paymentMethods,
        transactions: txnsRes.data.requests || txnsRes.data.transactions || [],
        announcements: (popupAnnRes.data.announcements || []).filter(isPopupAnnouncement),
        homeBanners: [
          ...(homeBannerRes.data.announcements || []),
          ...(popupAnnRes.data.announcements || []).filter(isImageBanner),
        ].filter((item, index, items) => item?.image_url && items.findIndex((x) => x.id === item.id) === index),
        accounts: accsRes.data.accounts || [],
        ledger: ledgerRes.data.entries || [],
      });
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    const refresh = async () => {
      setRefreshing(true);
      setPullDistance(0);
      try {
        await Promise.allSettled([
          fetchData({ silent: true }),
          typeof checkAuth === 'function' ? checkAuth() : Promise.resolve(),
        ]);
      } finally {
        setRefreshing(false);
      }
    };
    window.addEventListener('vl:player-soft-refresh', refresh);
    return () => window.removeEventListener('vl:player-soft-refresh', refresh);
    // fetchData is intentionally omitted: this listener only needs the latest
    // stable auth refresh and normal API calls will update state on completion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAuth]);

  const refreshGameData = async () => {
    try {
      // Cache-bust query param + no-store headers force any intermediate
      // cache (service worker leftovers, CDN, proxy) to hit the origin.
      // Without this, after a fresh create-account the UI can display a
      // 30-second-old account list served from the stale-while-revalidate
      // API cache.
      const bust = `_=${Date.now()}`;
      const noStore = { headers: { 'Cache-Control': 'no-cache, no-store' } };
      const [accsRes, walletRes] = await Promise.all([
        api.get(`/api/games/accounts?${bust}`, noStore).catch(() => ({ data: { accounts: [] } })),
        api.get(`/api/wallet/balance?${bust}`, noStore).catch(() => ({ data: { balance: 0 } })),
      ]);
      setWallet(walletRes.data);
      setData(prev => ({ ...prev, accounts: accsRes.data.accounts || [] }));
      // Do not auto-refresh provider balances here. Provider balance is now
      // manual-only to avoid upstream frequent-activity blocks.
    } catch {}
  };

  // ── Provider-balance refresh policy ──────────────────────────────────
  // Provider balances are manual-only. Do not poll /refresh-balances here;
  // players can use the Check Balance button on a game card when they need
  // a fresh provider balance. This prevents upstream frequent-activity blocks.

  // Lightweight refresh for transactions + wallet — used by the live-status
  // poller when the player has pending gateway deposits waiting on the
  // provider's webhook confirmation. No setLoading(true) so the UI doesn't
  // flash during every poll tick.
  const refreshTransactions = async () => {
    try {
      const bust = `_=${Date.now()}`;
      const noStore = { headers: { 'Cache-Control': 'no-cache, no-store' } };
      const [txnsRes, ledgerRes, walletRes] = await Promise.all([
        api.get(`/api/requests/my-requests?limit=20&${bust}`, noStore).catch(() => ({ data: { requests: [] } })),
        api.get(`/api/wallet/transactions?limit=20&${bust}`, noStore).catch(() => ({ data: { transactions: [] } })),
        api.get(`/api/wallet/balance?${bust}`, noStore).catch(() => ({ data: { balance: 0 } })),
      ]);
      setWallet(walletRes.data);
      setData(prev => ({
        ...prev,
        transactions: txnsRes.data.requests || txnsRes.data.transactions || [],
        ledger: ledgerRes.data.entries || prev.ledger,
      }));
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = tenantSlug ? `/${tenantSlug}/auth` : '/auth';
  };

  const copyReferral = () => {
    if (tenantSlug) return;
    navigator.clipboard.writeText(`${window.location.origin}/auth?ref=${user?.referral_code}`);
    toast.success('Referral link copied!');
  };

  const handleDailyCheckin = async () => {
    try {
      const res = await api.post('/api/wallet/daily-checkin');
      if (res.data.already_claimed) {
        toast.info('You already claimed today\'s bonus!');
      } else {
        // Iter 137: celebratory burst on successful claim — coin shower
        // + checkmark + amount label. Replaces the plain toast.
        setSuccessBurst({
          title: 'Bonus claimed!',
          subtitle: `+${res.data.reward} FP added`,
          color: tierTheme.primary,
          duration: 1900,
        });
        await checkAuth();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    }
  };

  if (loading) {
    return (
      <div className="vl-site-loader vl-site-loader--video" role="status" aria-label="Loading Vegas Lobby">
        <video
          className="vl-site-loader__video"
          src="/assets/vegas-lobby-intro.mp4"
          autoPlay
          muted
          playsInline
          preload="auto"
        />
        <div className="vl-site-loader__shade" />
      </div>
    );
  }

  return (
      <div
        className="vl-player-shell flex flex-col overflow-hidden"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: viewport.height || '100dvh',
        background: tierTheme.bg,
        paddingTop: 'env(safe-area-inset-top)',
        '--player-header-h': '58px',
        '--player-nav-h': viewport.keyboardOpen ? '0px' : '64px',
      }}
      data-keyboard-open={viewport.keyboardOpen ? 'true' : 'false'}
      data-casino-theme={casinoTheme.id}
      data-testid="player-app"
    >
      <CasinoAtmosphere activeTab={activeTab} />
      {casinoTheme.id === 'pirate' && <PirateLobbyExperience />}
      {/* Header — PERMANENT: outside swipe container, never shifts. Double-tap → scroll to top. */}
      <header className="vl-casino-header flex-shrink-0 z-50 px-3.5 py-2 cursor-pointer select-none" onClick={handleHeaderDoubleTap} style={{ backdropFilter: 'blur(18px)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2.5">
          <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); switchTab('brand'); }}
            className="flex items-center cursor-pointer"
            data-testid="logo-brand-page"
            aria-label="Vegas Lobby brand page"
          >
            {tenantLogoUrl ? (
              <img
                src={mediaUrl(tenantLogoUrl)}
                alt={tenantName}
                className="vl-casino-logo vl-casino-logo--topbar h-14 w-auto rounded-xl object-contain"
                style={{ filter: 'drop-shadow(0 2px 6px rgba(212,175,55,0.24))' }}
              />
            ) : (
              <img
                src="/vegas-lobby-logo.png"
                alt="Vegas Lobby"
                className="vl-casino-logo vl-casino-logo--topbar h-11 w-auto"
                style={{ filter: 'drop-shadow(0 2px 4px rgba(212,175,55,0.18))' }}
              />
            )}
          </button>
          </div>

          <button
            type="button"
            className="vl-captain-chip"
            onClick={(e) => { e.stopPropagation(); switchTab('profile'); }}
            data-testid="captain-profile-chip"
          >
            <span className="vl-captain-chip__avatar">{String(user?.username || user?.email || 'P').charAt(0).toUpperCase()}</span>
            <span className="vl-captain-chip__copy">
              <small>Captain aboard</small>
              <strong>{user?.username || user?.full_name || 'Player'}</strong>
            </span>
            <span className="vl-captain-chip__online" aria-label="Online" />
          </button>

          <div className="vl-casino-header__actions flex items-center gap-2">
            {/* Balance pill — wallet $ (main). FP credits get their own pill.
                Iter 133: AnimatedCounter for buttery balance roll-ups. */}
            <div
              className="vl-casino-balance vl-treasure-balance vl-topbar-balance-pill rounded-full anim-press cursor-pointer"
              style={{ background: tierTheme.balanceBg, border: tierTheme.balanceBorder }}
              data-testid="balance-pill"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setDepositModal('deposit'); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDepositModal('deposit'); }}
            >
              <span
                className="vl-topbar-balance-coin inline-flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ width: 21, height: 21 }}
                data-testid="sc-coin-icon-wrap"
              >
                <img
                  src="/sc-coin.png"
                  alt="$"
                  className="sc-coin-spin"
                  style={{
                    width: 21,
                    height: 21,
                    maxWidth: 21,
                    maxHeight: 21,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 0 4px rgba(212,175,55,0.45))',
                  }}
                  data-testid="sc-coin-icon"
                />
              </span>
              <span className="vl-treasure-balance__copy vl-top-cash-balance vl-topbar-cash" data-testid="top-cash-balance">
                <span className="vl-topbar-cash-value inline-flex items-baseline gap-1">
                  {balanceVisible ? (
                    <AnimatedCounter
                      value={wallet?.balance || user?.wallet_balance || 0}
                      decimals={2}
                      minDecimals={2}
                      duration={500}
                      prefix="$"
                      className="vl-top-cash-balance__value font-black text-[15px]"
                      style={{ color: tierTheme.primary }}
                    />
                  ) : (
                    <span className="vl-top-cash-balance__value font-black text-[15px] tabular-nums" style={{ color: tierTheme.primary }}>****</span>
                  )}
                </span>
              </span>
              <span className="vl-top-balance-divider h-4 w-px bg-white/10" />
              <span className="vl-top-fp-balance vl-topbar-fp inline-flex items-baseline gap-1" data-testid="top-fp-balance">
                <span className="text-[10px] font-black tracking-wide" style={{ color: '#A5B4FC' }}>FP</span>
                {balanceVisible ? (
                  <AnimatedCounter
                    value={wallet?.fp_credits ?? wallet?.play_credits ?? 0}
                    decimals={0}
                    minDecimals={0}
                    duration={500}
                    className="font-black text-[13px]"
                    style={{ color: '#C7D2FE' }}
                  />
                ) : (
                  <span className="font-black text-[13px] tabular-nums" style={{ color: '#C7D2FE' }}>**</span>
                )}
              </span>
              <button onClick={(e) => { e.stopPropagation(); setBalanceVisible(!balanceVisible); }} className="hidden" data-testid="toggle-balance" aria-hidden="true" tabIndex={-1}>
                {balanceVisible ? <Eye className="w-3.5 h-3.5" style={{ color: '#F5D76E' }} /> : <EyeOff className="w-3.5 h-3.5" style={{ color: '#F5D76E' }} />}
              </button>
            </div>
            <button onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) fetchNotifications(); }} className="p-2 rounded-xl hover:bg-white/5 relative anim-press transition-colors duration-200" data-testid="notif-bell" aria-label="Notifications">
              <Bell className="w-[22px] h-[22px]" style={{ color: '#F5D76E', filter: 'drop-shadow(0 0 3px rgba(245,215,110,0.22))' }} />
              {notifCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center anim-notif-pop">{notifCount > 9 ? '9+' : notifCount}</span>
              )}
            </button>
            <button onClick={() => { setShowAnnDrop(!showAnnDrop); if (!showAnnDrop) fetchAnnouncements(); }} className="p-2 rounded-xl hover:bg-white/5 relative anim-press transition-colors duration-200" data-testid="announcements-bell" aria-label="Announcements">
              <Megaphone className="w-[22px] h-[22px]" style={{ color: '#F5D76E', filter: 'drop-shadow(0 0 3px rgba(245,215,110,0.22))' }} />
              {unseenCount > 0 ? (
                <>
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#D4AF37] text-black text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="announcements-badge">
                    {unseenCount > 9 ? '9+' : unseenCount}
                  </span>
                  <span
                    className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-[#D4AF37] animate-pulse"
                    style={{ boxShadow: '0 0 6px rgba(212,175,55,0.9)' }}
                    data-testid="announcements-unseen-dot"
                  />
                </>
              ) : announcements.length > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-white/10 text-gray-300 text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="announcements-badge">
                  {announcements.length > 9 ? '9+' : announcements.length}
                </span>
              ) : null}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const soundButton = document.querySelector('[data-testid="pirate-sound-toggle"]');
                if (soundButton) {
                  soundButton.click();
                  window.setTimeout(() => {
                    try { setHeaderSoundOn(localStorage.getItem('vl_pirate_sound') === 'on'); } catch { setHeaderSoundOn((value) => !value); }
                  }, 0);
                } else {
                  setHeaderSoundOn((value) => !value);
                }
              }}
              className="vl-header-sound-button p-2 rounded-xl hover:bg-white/5 relative anim-press transition-colors duration-200"
              data-testid="header-sound-toggle"
              aria-label={headerSoundOn ? 'Mute sound' : 'Play sound'}
            >
              {headerSoundOn ? (
                <Volume2 className="w-[21px] h-[21px]" style={{ color: '#F5D76E', filter: 'drop-shadow(0 0 3px rgba(245,215,110,0.24))' }} />
              ) : (
                <VolumeX className="w-[21px] h-[21px]" style={{ color: '#F5D76E', filter: 'drop-shadow(0 0 3px rgba(245,215,110,0.24))' }} />
              )}
            </button>
            <button onClick={() => switchTab('profile')} className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/5 text-gray-400 hover:text-white anim-press transition-colors duration-200" data-testid="profile-btn" aria-label="Profile">
              {avatarSrc ? (
                <img src={avatarSrc} alt="Profile" className="w-10 h-10 rounded-full object-cover border-2 border-[#D4AF37]/45 shadow-[0_0_16px_rgba(212,175,55,0.22)]" />
              ) : (
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#7A4B16] text-black text-lg font-black flex items-center justify-center border border-[#F5D76E]/45 shadow-[0_0_16px_rgba(212,175,55,0.22)]">
                  {avatarInitial}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Notification Dropdown */}
      {showNotifs && (
        <div className="fixed inset-0 z-[9998]" onClick={() => setShowNotifs(false)}>
          <div className="absolute top-14 right-2 left-2 sm:left-auto sm:right-4 sm:w-80 max-w-[420px] sm:max-w-none mx-auto sm:mx-0 rounded-2xl shadow-2xl overflow-hidden" style={{ background: '#1a1a2e', border: `1px solid ${tierTheme.primary}30` }} onClick={e => e.stopPropagation()} data-testid="notif-dropdown">
            <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <h3 className="text-sm font-bold text-white">Notifications</h3>
              {notifCount > 0 && <button onClick={markAllRead} className="text-[10px] font-medium" style={{ color: tierTheme.primary }}>Mark all read</button>}
            </div>
            {notifications.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm">No notifications</p>
            ) : (
              <>
                {notifications.slice(0, 5).map(n => (
                  <div key={n.id} className="px-3 py-2.5 cursor-pointer hover:bg-white/5 transition" onClick={() => { setSelectedNotif(n); setShowNotifs(false); }}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: n.read ? 'transparent' : 'rgba(212,175,55,0.03)' }}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: tierTheme.primary }} />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-white truncate">{n.title}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{n.message}</p>
                        <p className="text-[9px] text-gray-600 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {notifications.length > 5 && (
                  <button onClick={() => { setShowAllNotifs(true); setShowNotifs(false); }} className="w-full py-2.5 text-xs font-bold text-center transition hover:bg-white/5" style={{ color: tierTheme.primary, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    View All ({notifications.length})
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Announcements Dropdown (mirrors notifications UX; shows 5 latest) */}
      {showAnnDrop && (
        <div className="fixed inset-0 z-[9998]" onClick={() => setShowAnnDrop(false)}>
          <div
            className="absolute top-14 right-2 left-2 sm:left-auto sm:right-4 sm:w-80 max-w-[420px] sm:max-w-none mx-auto sm:mx-0 rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: '#1a1a2e', border: `1px solid ${tierTheme.primary}30` }}
            onClick={e => e.stopPropagation()}
            data-testid="announcements-dropdown"
          >
            <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" style={{ color: tierTheme.primary }} />
                <h3 className="text-sm font-bold text-white">Announcements</h3>
              </div>
              {announcements.length > 0 && (
                <span className="text-[10px] text-gray-400" data-testid="announcements-count">{announcements.length} active</span>
              )}
            </div>
            {announcements.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm" data-testid="announcements-empty">No announcements right now</p>
            ) : (
              <>
                {announcements.slice(0, 5).map(a => {
                  const isUnseen = !seenAnnIds.has(a.id);
                  return (
                    <div
                      key={a.id}
                      className="px-3 py-2.5 cursor-pointer hover:bg-white/5 transition"
                      onClick={() => { markAnnSeen(a.id); setSelectedAnnouncement(a); setShowAnnDrop(false); }}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                        background: isUnseen ? 'rgba(212,175,55,0.05)' : 'transparent',
                      }}
                      data-testid={`announcement-row-${a.id}`}
                      data-unseen={isUnseen ? '1' : '0'}
                    >
                      <div className="flex items-start gap-2">
                        {a.image_url ? (
                          <img
                            src={a.image_url.startsWith('http') ? a.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${a.image_url}`}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                            style={{ background: a.bg_color || tierTheme.primary, color: a.text_color || '#000' }}>
                            <Megaphone className="w-4 h-4" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className={`text-xs truncate ${isUnseen ? 'font-bold text-white' : 'font-medium text-gray-300'}`}>{a.title}</p>
                            {isUnseen && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" data-testid={`announcement-unseen-${a.id}`} />}
                          </div>
                          <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{a.content}</p>
                          <p className="text-[9px] text-gray-600 mt-1">
                            {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                            {a.banner_type ? ` · ${a.banner_type.replace('_', ' ')}` : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {announcements.length > 5 && (
                  <div className="w-full py-2.5 text-xs font-bold text-center" style={{ color: tierTheme.primary, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    +{announcements.length - 5} more — check back later
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Announcement Detail Popup — reuses the notif popup pattern */}
      {selectedAnnouncement && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setSelectedAnnouncement(null)}
        >
          <div
            className="rounded-2xl max-w-sm w-full overflow-hidden"
            style={{ background: '#1a1a2e', border: `1px solid ${tierTheme.primary}30` }}
            onClick={e => e.stopPropagation()}
            data-testid="announcement-detail-popup"
          >
            {selectedAnnouncement.image_url && (
              <img
                src={selectedAnnouncement.image_url.startsWith('http') ? selectedAnnouncement.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${selectedAnnouncement.image_url}`}
                alt={selectedAnnouncement.title}
                className="w-full h-36 object-cover"
              />
            )}
            <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" style={{ color: tierTheme.primary }} />
                <h3 className="text-base font-bold text-white">Announcement</h3>
              </div>
              <button onClick={() => setSelectedAnnouncement(null)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5">
              <h4 className="text-lg font-bold text-white mb-2">{selectedAnnouncement.title}</h4>
              {selectedAnnouncement.content && (
                <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{selectedAnnouncement.content}</p>
              )}
              {(selectedAnnouncement.redirect_url || selectedAnnouncement.link_url) && (
                <a
                  href={selectedAnnouncement.redirect_url || selectedAnnouncement.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 px-4 py-2 rounded-xl text-sm font-bold transition hover:scale-105"
                  style={{ background: `linear-gradient(135deg, ${tierTheme.primary}, ${tierTheme.accent})`, color: '#000' }}
                  data-testid="announcement-cta-link"
                >
                  Learn more →
                </a>
              )}
              <p className="text-[10px] text-gray-600 mt-4">
                {selectedAnnouncement.created_at ? new Date(selectedAnnouncement.created_at).toLocaleString() : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notification Detail Popup */}
      {selectedNotif && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setSelectedNotif(null)}>
          <div className="rounded-2xl max-w-sm w-full overflow-hidden" style={{ background: '#1a1a2e', border: `1px solid ${tierTheme.primary}30` }} onClick={e => e.stopPropagation()} data-testid="notif-detail-popup">
            <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <h3 className="text-base font-bold text-white">Notification</h3>
              <button onClick={() => setSelectedNotif(null)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5">
              <h4 className="text-lg font-bold text-white mb-2">{selectedNotif.title}</h4>
              <p className="text-sm text-gray-300 leading-relaxed">{selectedNotif.message}</p>
              <p className="text-[10px] text-gray-600 mt-4">{new Date(selectedNotif.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* All Notifications Fullscreen */}
      {showAllNotifs && (
        <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'linear-gradient(180deg, #0f0a1e 0%, #0a0e17 100%)' }} data-testid="all-notifs-popup">
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <h2 className="text-lg font-bold text-white">All Notifications</h2>
            <button onClick={() => setShowAllNotifs(false)} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10"><X className="w-6 h-6 text-gray-400" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
            {notifications.map(n => (
              <div key={n.id} onClick={() => { setSelectedNotif(n); setShowAllNotifs(false); }}
                className="rounded-xl p-3 cursor-pointer hover:bg-white/5 transition" style={{ background: n.read ? 'rgba(255,255,255,0.02)' : 'rgba(212,175,55,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: tierTheme.primary }} />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{n.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-gray-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Push Notification Prompt */}
      {showPushPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={dismissPushPrompt}>
          <div className="rounded-2xl p-6 max-w-sm w-full text-center" style={{ background: '#1a1a2e', border: `1px solid ${tierTheme.primary}30` }} onClick={e => e.stopPropagation()} data-testid="push-prompt">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${tierTheme.primary}20` }}>
              <BellRing className="w-8 h-8" style={{ color: tierTheme.primary }} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Enable Notifications</h3>
            <p className="text-sm text-gray-400 mb-5">Don't miss chances to win rewards, bonuses & exclusive offers!</p>
            <div className="flex gap-3">
              <button onClick={dismissPushPrompt} className="flex-1 py-3 rounded-xl text-sm font-medium text-gray-400 hover:bg-white/5 transition" data-testid="dismiss-push-btn">Later</button>
              <button onClick={handleEnablePush} className="flex-1 py-3 rounded-xl text-sm font-bold text-black transition hover:scale-105" style={{ background: `linear-gradient(135deg, ${tierTheme.primary}, ${tierTheme.accent})` }} data-testid="enable-push-btn">Enable</button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Popup */}
      {showAnnouncement && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center px-6" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => dismissAnnouncement(showAnnouncement.id)}>
          <div className="rounded-2xl overflow-hidden max-w-sm w-full relative" style={{ background: '#1a1a2e', border: `1px solid ${tierTheme.primary}30` }} onClick={e => e.stopPropagation()} data-testid="announcement-popup">
            <button onClick={() => dismissAnnouncement(showAnnouncement.id)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center bg-black/50 hover:bg-black/70 transition" data-testid="close-announcement">
              <X className="w-4 h-4 text-white" />
            </button>
            {showAnnouncement.image_url && (
              <img src={showAnnouncement.image_url?.startsWith('http') ? showAnnouncement.image_url : `${process.env.REACT_APP_BACKEND_URL}${showAnnouncement.image_url}`} alt="" className="w-full h-48 object-cover" />
            )}
            <div className="p-5">
              <h3 className="text-lg font-bold text-white mb-2">{showAnnouncement.title}</h3>
              {showAnnouncement.content && <p className="text-sm text-gray-400 mb-3">{showAnnouncement.content}</p>}
              {(showAnnouncement.redirect_url || showAnnouncement.link_url) && (
                <a href={showAnnouncement.redirect_url || showAnnouncement.link_url} target="_blank" rel="noopener noreferrer"
                  className="block w-full py-3 rounded-xl text-sm font-bold text-black text-center transition hover:scale-105"
                  style={{ background: `linear-gradient(135deg, ${tierTheme.primary}, ${tierTheme.accent})` }}>
                  Learn More
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Slide + Level Up animation styles */}
      <style>{`
        @keyframes slideInLeft { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(-40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .slide-left { animation: slideInLeft 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
        .slide-right { animation: slideInRight 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
        @keyframes lvlPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }
        @keyframes lvlGlow { 0% { box-shadow: 0 0 12px var(--lvl-color); } 50% { box-shadow: 0 0 28px var(--lvl-color); } 100% { box-shadow: 0 0 12px var(--lvl-color); } }
        @keyframes lvlFadeIn { from { opacity:0; transform:scale(0.8) translateY(20px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes lvlParticle { 0% { transform:translateY(0) scale(1); opacity:1; } 100% { transform:translateY(-60px) scale(0); opacity:0; } }
        @keyframes lvlFadeOut { 0% { opacity:1; } 70% { opacity:1; } 100% { opacity:0; } }
        .vl-main-tab-copy { font-size: 1.08rem; }
        .vl-main-tab-copy .text-\\[8px\\] { font-size: 9px !important; }
        .vl-main-tab-copy .text-\\[9px\\] { font-size: 10.5px !important; }
        .vl-main-tab-copy .text-\\[10px\\] { font-size: 11.5px !important; }
        .vl-main-tab-copy .text-\\[11px\\] { font-size: 12.5px !important; }
        .vl-main-tab-copy .text-\\[12px\\] { font-size: 13.5px !important; }
        .vl-main-tab-copy .text-xs { font-size: .86rem !important; line-height: 1.2rem !important; }
        .vl-main-tab-copy .text-sm { font-size: .99rem !important; line-height: 1.38rem !important; }
        .vl-main-tab-copy .text-base { font-size: 1.12rem !important; line-height: 1.52rem !important; }
        .vl-main-tab-copy .text-lg { font-size: 1.28rem !important; line-height: 1.7rem !important; }
        .vl-main-tab-copy .text-xl { font-size: 1.42rem !important; line-height: 1.86rem !important; }
        .vl-main-tab-copy .text-2xl { font-size: 1.68rem !important; line-height: 2.08rem !important; }
        .vl-main-tab-copy .text-3xl { font-size: 2.05rem !important; line-height: 2.42rem !important; }
      `}</style>

      {/* Level Up Overlay — PUBG/Free-Fire style cinematic rank-up */}
      <LevelUpOverlay data={levelUpData} onClose={() => setLevelUpData(null)} />

      {/* SuccessBurst — celebratory overlay for bonus claims, account
          creation, deposit confirmations. State driven via setSuccessBurst. */}
      <SuccessBurst
        open={!!successBurst}
        title={successBurst?.title}
        subtitle={successBurst?.subtitle}
        color={successBurst?.color || tierTheme.primary}
        duration={successBurst?.duration || 1800}
        onDone={() => setSuccessBurst(null)}
      />

      {/* Main Content — horizontal strip of all tabs for OS-like continuous swipe */}
      {activeTab === 'chat' ? (
        <main className="vl-chat-page flex-1 max-w-7xl mx-auto w-full overflow-hidden px-2 pb-2 relative" style={{ minHeight: 0 }}>
          <ChatTab user={user} onRead={() => setChatUnread(0)} tierTheme={tierTheme} />
        </main>
      ) : activeTab === 'settings' ? (
        <main className="flex-1 max-w-7xl mx-auto w-full overflow-y-auto overflow-x-hidden px-4 py-4 relative" style={{ minHeight: 0 }}>
          <SettingsTab user={user} checkAuth={checkAuth} goBack={goBack} onLogout={handleLogout} />
          <DepositWithdrawPopup
            mode={depositModal}
            onClose={() => setDepositModal(null)}
            user={user} wallet={wallet} methods={data.methods}
            refresh={fetchData} checkAuth={checkAuth}
            onAfterSubmit={handleDepositSubmitted}
          />
        </main>
      ) : activeTab === 'profile' ? (
        <main className="flex-1 max-w-7xl mx-auto w-full overflow-y-auto overflow-x-hidden px-0 py-0 relative" style={{ minHeight: 0 }}>
          <ProfileTab user={user} myTier={myTier} goBack={goBack} onSettings={() => switchTab('settings')} checkAuth={checkAuth} />
          <DepositWithdrawPopup
            mode={depositModal}
            onClose={() => setDepositModal(null)}
            user={user} wallet={wallet} methods={data.methods}
            refresh={fetchData} checkAuth={checkAuth}
            onAfterSubmit={handleDepositSubmitted}
          />
        </main>
      ) : activeTab === 'brand' ? (
        <main className="flex-1 max-w-7xl mx-auto w-full overflow-y-auto overflow-x-hidden px-0 py-0 relative" style={{ minHeight: 0 }}>
          <BrandTab goBack={goBack} tenantName={tenantName} logoUrl={tenantLogoUrl || '/vegas-lobby-logo.png'} supportChannels={supportChannels} games={data.games} />
          <DepositWithdrawPopup
            mode={depositModal}
            onClose={() => setDepositModal(null)}
            user={user} wallet={wallet} methods={data.methods}
            refresh={fetchData} checkAuth={checkAuth}
            onAfterSubmit={handleDepositSubmitted}
          />
        </main>
      ) : (
        <main
          ref={mainRef}
          className="vl-content-stage flex-1 w-full overflow-hidden relative"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          style={{ minHeight: 0, touchAction: 'pan-y' }}
        >
          {/* Pull-to-refresh indicator */}
          {(pullDistance > 0 || refreshing) && (
            <div
              className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-20"
              style={{
                top: 0,
                height: refreshing ? 56 : pullDistance,
                transition: pullRef.current.tracking ? 'none' : 'height 0.25s ease-out',
              }}
              data-testid="pull-refresh-indicator"
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                  transform: `rotate(${refreshing ? 0 : Math.min(pullDistance * 3, 360)}deg)`,
                  opacity: Math.min(pullDistance / 60, 1),
                }}
              >
                {refreshing ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                    <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                  </svg>
                )}
              </div>
            </div>
          )}
          <div
            className="flex h-full"
            style={{
              width: `${TAB_ORDER.length * 100}%`,
              transform: `translateX(calc(-${TAB_ORDER.indexOf(activeTab) * (100 / TAB_ORDER.length)}% + ${dragX}px)) translateY(${pullDistance}px)`,
              transition: (dragging || pullRef.current.tracking) ? 'none' : 'transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              willChange: 'transform',
            }}
          >
            {TAB_ORDER.map((tabId) => {
              return (
                <div
                  key={tabId}
                  className="h-full overflow-y-auto"
                  style={{ width: `${100 / TAB_ORDER.length}%`, flexShrink: 0 }}
                  data-testid={`swipe-panel-${tabId}`}
                >
                  <div className="vl-main-tab-copy max-w-7xl mx-auto px-4 pt-2.5 pb-2">
                    {tabId === 'home' && <HomeTab user={user} wallet={wallet} data={data} onDailyCheckin={handleDailyCheckin} copyReferral={copyReferral} showReferral={!tenantSlug} onReferralClick={goToReferralRewards} onPhoneVerifyClick={() => setShowPhoneVerification(true)} onGameClick={(gameId) => { switchTab('games'); if (gameId) window.__expandGameId = gameId; }} onDeposit={() => setDepositModal('deposit')} onWithdraw={() => setDepositModal('withdrawal')} tierTheme={tierTheme} balanceVisible={balanceVisible} onToggleBalance={() => setBalanceVisible(!balanceVisible)} onWalletRefresh={fetchData} />}
                    {tabId === 'games' && <GamesTab games={data.games} accounts={data.accounts} wallet={wallet} refresh={refreshGameData} onDeposit={() => setDepositModal('deposit')} onWithdraw={() => setDepositModal('withdrawal')} initialGameId={window.__expandGameId} balanceVisible={balanceVisible} onToggleBalance={() => setBalanceVisible(!balanceVisible)} activeTab={activeTab} onOptimisticAccount={(acc) => setData(prev => ({ ...prev, accounts: [...(prev.accounts || []).filter(a => a.id !== acc.id), acc] }))} />}
                    {tabId === 'referrals' && <ReferralsTab user={user} copyReferral={copyReferral} goBack={goBack} />}
                    {tabId === 'rewards' && <RewardsTab user={user} wallet={wallet} copyReferral={copyReferral} goBack={goBack} refreshWallet={refreshTransactions} initialSubTab={rewardsInitialSubTab} />}
                    {tabId === 'profile' && <ProfileTab user={user} myTier={myTier} goBack={goBack} onSettings={() => switchTab('settings')} checkAuth={checkAuth} />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Deposit / Withdraw popup — constrained to main area (between header and nav) */}
          <DepositWithdrawPopup
            mode={depositModal}
            onClose={() => setDepositModal(null)}
            user={user} wallet={wallet} methods={data.methods}
            refresh={fetchData} checkAuth={checkAuth}
            onAfterSubmit={handleDepositSubmitted}
          />

          {/* Popup host — game detail popup (and other overlays) portal here */}
          <div id="player-popup-host" className="absolute inset-0 pointer-events-none z-30" />
        </main>
      )}

      {!viewport.keyboardOpen && activeTab !== 'chat' && !depositModal && (
      <button
        type="button"
        onClick={openLiveSupport}
        className="vl-support-orb fixed right-4 z-[70] w-[58px] h-[58px] rounded-full flex items-center justify-center anim-press transition-transform duration-200 hover:scale-[1.03] active:scale-95"
        style={{
          bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
          background: '#fff',
          boxShadow: '0 0 0 5px rgba(0, 132, 255, 0.06), 0 10px 24px rgba(0,0,0,0.36)',
        }}
        aria-label="Open live support"
        data-testid="floating-live-support"
      >
        <MessageCircle className="w-6 h-6" aria-hidden="true" />
      </button>
      )}

      {/* Bottom Navigation — PERMANENT, outside swipe container. Iter 140:
          extracted into PlayerBottomNav.jsx for code-organisation. */}
      {!viewport.keyboardOpen && !depositModal && (
        <PlayerBottomNav
          tabs={TABS}
          activeTab={activeTab}
          onSwitch={switchTab}
          tierTheme={tierTheme}
        />
      )}

      {/* Manual-deposit flow notifications: "please wait" → "wallet loaded" (or "declined") */}
      <WalletNotificationModal
        open={!!walletNotif}
        variant={walletNotif?.variant || 'pending'}
        amount={walletNotif?.creditedDelta || walletNotif?.amountUsd}
        reason={walletNotif?.rejectedReason}
        onClose={() => setWalletNotif(null)}
        onGoToRewards={() => switchTab('rewards')}
        autoDismissMs={walletNotif?.variant === 'credited' ? 7000 : (walletNotif?.variant === 'rejected' ? 10000 : 9000)}
      />

      <PhoneVerificationGate
        open={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        onVerified={async () => {
          setShowPhoneVerification(false);
          await checkAuth?.();
          await fetchData?.();
        }}
      />

    </div>
  );
}
